/**
 * DASHBOARD SERVICE - Orquestador de todos los endpoints WordPress
 * Aprovecha todos los servicios existentes para crear una vista unificada
 */

import axios from 'axios';
import { createWordPressAPIInstance, getOverduePayments, getPaymentStats, getPaymentsWithFilters } from './billing';
import { getCustomers } from './woo';
import { getEvents } from './agenda';
import { getAllSurveyResponses, getUserAuth } from './surveyService';
import { canUserAccessCategory } from '../utils/site';

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
const cache = new Map();

/**
 * Helper para cache con expiración
 */
const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
  return data;
};

/**
 * Obtiene datos principales del dashboard
 * @returns {Promise<Object>} Datos consolidados del CRM
 */
export const getDashboardData = async () => {
  const cacheKey = 'dashboard_main_data';
  const cached = getCachedData(cacheKey);
  if (cached) {
    return cached;
  }
  
  try {
    // Usar servicios reales siguiendo patrón exitoso de Suscriptions y Billing
    const [
      usersResponse,
      paymentStatsResponse,
      completedPaymentsResponse,
      pendingPaymentsResponse,
      overduePaymentsResponse
    ] = await Promise.allSettled([
      // 1. Usuarios reales siguiendo patrón exitoso de Suscriptions/index.js
      getCustomers(
        '', // suscriptionName
        '', // groupName  
        '', // search
        null, // startDate
        null, // endDate
        '', // role
        1, // page
        20 // perPage (reducido para dashboard)
      ),
      
      // 2. Estadísticas de pagos reales desde billing.js
      getPaymentStats({ 
        start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0] 
      }),
      
      // 3. Pagos completados siguiendo patrón exitoso de Billing/index.js
      getPaymentsWithFilters(
        { status: 'completed' }, // un solo estado como en Billing
        { page: 1, per_page: 10 }
      ),
      
      // 4. Pagos pendientes siguiendo patrón exitoso de Billing/index.js
      getPaymentsWithFilters(
        { status: 'pending' }, // un solo estado como en Billing
        { page: 1, per_page: 5 }
      ),
      
      // 5. Pagos atrasados reales (este funciona)
      getOverduePayments(30)
    ]);

    // Procesar respuestas con manejo de errores
    const processResponse = (response, defaultValue = null) => {
      if (response.status === 'fulfilled') {
        // Para servicios que devuelven {success: true, data: ...}
        if (response.value?.success) {
          return response.value.data;
        }
        // Para servicios que devuelven directamente data
        return response.value;
      }
      return defaultValue;
    };

    // Procesar usuarios siguiendo patrón exitoso de Suscriptions/index.js
    const usersData = usersResponse.status === 'fulfilled' ? usersResponse.value : null;
    const users = (usersData?.data && Array.isArray(usersData.data)) ? usersData.data : [];
    const usersTotal = usersData?.headers ? parseInt(usersData.headers['x-wp-total']) || 0 : users.length;
    
    // Procesar estadísticas de pagos
    const paymentStats = paymentStatsResponse.status === 'fulfilled' ? paymentStatsResponse.value : {};
    
    // Procesar pagos completados siguiendo patrón exitoso de Billing/index.js
    const completedPaymentsData = completedPaymentsResponse.status === 'fulfilled' ? completedPaymentsResponse.value : { data: [] };
    const completedPayments = (completedPaymentsData?.data && Array.isArray(completedPaymentsData.data)) ? completedPaymentsData.data : [];
    
    // Procesar pagos pendientes siguiendo patrón exitoso de Billing/index.js
    const pendingPaymentsData = pendingPaymentsResponse.status === 'fulfilled' ? pendingPaymentsResponse.value : { data: [] };
    const pendingPayments = (pendingPaymentsData?.data && Array.isArray(pendingPaymentsData.data)) ? pendingPaymentsData.data : [];
    
    // Combinar pagos recientes (completados + pendientes)
    const recentPayments = [...completedPayments, ...pendingPayments]
      .sort((a, b) => new Date(b.date_created) - new Date(a.date_created))
      .slice(0, 15);
    
    // Procesar pagos atrasados (igual que en Billing)
    const overduePayments = overduePaymentsResponse.status === 'fulfilled' 
      ? overduePaymentsResponse.value 
      : { data: [], total: 0, total_amount: 0 };

    // Calcular métricas consolidadas usando datos reales con validación
    const dashboardData = {
      // Métricas de usuarios reales usando patrón exitoso
      users: {
        total: usersTotal,
        active: users.filter(u => u.date_created || u.id).length,
        data: users.slice(0, 10), // Mostrar últimos 10 
        recent: users.slice(0, 5).map(user => ({
          id: user.id,
          name: user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Sin nombre',
          rut: user.username || 'Sin RUT',
          email: user.email || 'Sin email',
          date: user.date_created
        }))
      },
      
      // Métricas de pagos reales usando datos procesados correctamente
      payments: {
        completed: paymentStats?.total_collected || 0,
        pending: paymentStats?.pending_amount || 0,
        pendingCount: pendingPaymentsData?.total || 0,
        monthly: paymentStats?.total_collected || 0,
        count: (completedPayments.length + pendingPayments.length),
        overdue: {
          count: overduePayments?.total || 0,
          amount: overduePayments?.total_amount || 0,
          critical: (overduePayments?.data && Array.isArray(overduePayments.data)) 
            ? overduePayments.data.filter(p => p.days_overdue > 60).length 
            : 0
        }
      },
      
      // Agenda - por ahora datos simulados hasta implementar agenda.js
      agenda: {
        thisWeek: 0,
        thisMonth: 0,
        upcoming: 0,
        conflicts: 0
      },
      
      // Estado del sistema simplificado
      system: {
        status: 'ok',
        version: '1.0',
        lastCheck: new Date().toISOString(),
        errors: []
      },
      
      // Actividad reciente - combinar pagos y usuarios recientes
      recentActivity: [
        // Pagos recientes
        ...recentPayments.slice(0, 8).map(payment => ({
          id: `payment_${payment.id}`,
          type: 'payment',
          title: `Pago ${payment.status === 'completed' ? 'completado' : payment.status === 'pending' ? 'pendiente' : payment.status}`,
          description: `${payment.billing?.first_name || 'Usuario'} ${payment.billing?.last_name || ''} - $${parseInt(payment.total || 0).toLocaleString()} CLP`,
          timestamp: payment.date_created || payment.date_paid || new Date().toISOString(),
          status: payment.status,
          data: payment
        })),
        // Usuarios recientes
        ...users.slice(0, 4).map(user => ({
          id: `user_${user.id}`,
          type: 'user',
          title: 'Usuario registrado',
          description: `${user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Usuario nuevo'} - ${user.email || 'Sin email'}`,
          timestamp: user.date_created || new Date().toISOString(),
          status: 'active',
          data: user
        }))
      ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 10),
      
      // Timestamp de generación
      generatedAt: new Date().toISOString(),
      
      // Metadata de las llamadas reales
      metadata: {
        usersCall: usersResponse.status,
        paymentsCall: paymentStatsResponse.status,
        completedPaymentsCall: completedPaymentsResponse.status,
        pendingPaymentsCall: pendingPaymentsResponse.status,
        overdueCall: overduePaymentsResponse.status
      }
    };


    return setCachedData(cacheKey, dashboardData);

  } catch (error) {
    console.error('❌ DASHBOARD SERVICE: Error obteniendo datos:', error);
    throw new Error(`Error del dashboard: ${error.message}`);
  }
};

/**
 * Obtiene alertas críticas que requieren atención inmediata
 * @returns {Promise<Object>} Alertas críticas del sistema
 */
export const getCriticalAlerts = async () => {
  const cacheKey = 'dashboard_critical_alerts';
  const cached = getCachedData(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    
    // Solo usar servicios que realmente funcionan
    const [
      criticalOverdueResponse
    ] = await Promise.allSettled([
      // Pagos críticamente atrasados (60+ días) - ESTE SÍ FUNCIONA
      getOverduePayments(60)
    ]);

    const criticalOverdue = criticalOverdueResponse.status === 'fulfilled' 
      ? criticalOverdueResponse.value 
      : { data: [], total: 0, total_amount: 0 };

    const alertsData = {
      // Pagos críticos (datos reales)
      criticalPayments: {
        count: criticalOverdue?.total || 0,
        amount: criticalOverdue?.total_amount || 0,
        items: criticalOverdue?.data?.slice(0, 5) || [],
        severity: criticalOverdue?.total > 10 ? 'high' : criticalOverdue?.total > 5 ? 'medium' : 'low'
      },
      
      // Conflictos de agenda (simulado por ahora - agenda.js no implementado)
      agendaConflicts: {
        count: 0,
        items: [],
        severity: 'low'
      },
      
      // Errores del sistema (simulado - sin endpoint real)
      systemIssues: {
        count: 0,
        items: [],
        severity: 'low'
      },
      
      // Resumen general
      totalAlerts: criticalOverdue?.total || 0,
      
      generatedAt: new Date().toISOString()
    };


    return setCachedData(cacheKey, alertsData);

  } catch (error) {
    console.error('❌ DASHBOARD SERVICE: Error obteniendo alertas críticas:', error);
    // Devolver estructura vacía en lugar de error
    return {
      criticalPayments: { count: 0, amount: 0, items: [], severity: 'low' },
      agendaConflicts: { count: 0, items: [], severity: 'low' },
      systemIssues: { count: 0, items: [], severity: 'low' },
      totalAlerts: 0,
      generatedAt: new Date().toISOString()
    };
  }
};

/**
 * Obtiene timeline unificado de actividad reciente
 * @returns {Promise<Array>} Timeline de actividad combinada
 */
export const getUnifiedActivityTimeline = async () => {
  try {
    
    const [
      completedPaymentsResponse,
      pendingPaymentsResponse
    ] = await Promise.allSettled([
      // Usar patrón exitoso de llamadas separadas
      getPaymentsWithFilters({ status: 'completed' }, { page: 1, per_page: 10 }),
      getPaymentsWithFilters({ status: 'pending' }, { page: 1, per_page: 5 })
    ]);

    // Procesar respuestas siguiendo patrón exitoso de Billing
    const completedPaymentsData = completedPaymentsResponse.status === 'fulfilled' ? completedPaymentsResponse.value : { data: [] };
    const completedPayments = (completedPaymentsData?.data && Array.isArray(completedPaymentsData.data)) ? completedPaymentsData.data : [];
    
    const pendingPaymentsData = pendingPaymentsResponse.status === 'fulfilled' ? pendingPaymentsResponse.value : { data: [] };
    const pendingPayments = (pendingPaymentsData?.data && Array.isArray(pendingPaymentsData.data)) ? pendingPaymentsData.data : [];
    
    // Combinar y ordenar pagos
    const recentPayments = [...completedPayments, ...pendingPayments]
      .sort((a, b) => new Date(b.date_created) - new Date(a.date_created));

    // Obtener también usuarios recientes, eventos de agenda y respuestas de formularios para el timeline
    const [
      recentUsersResponse,
      recentEventsResponse,
      surveyResponsesResponse
    ] = await Promise.allSettled([
      getCustomers('', '', '', null, null, '', 1, 8), // Últimos 8 usuarios (más para timeline)
      getEvents({ 
        limit: 6,
        page: 1,
        orderBy: 'created_at',
        orderDirection: 'desc'
      }), // Últimos eventos de agenda
      getAllSurveyResponses({ 
        orderBy: 'created_at',
        orderDirection: 'desc',
        limit: 10
      }) // Obtener más respuestas de formularios recientes
    ]);

    // Procesar usuarios recientes - array directo
    const recentUsersData = recentUsersResponse.status === 'fulfilled' ? recentUsersResponse.value : null;
    const recentUsers = Array.isArray(recentUsersData?.data) ? recentUsersData.data : [];

    // Procesar eventos de agenda recientes - viene en .data
    const recentEventsData = recentEventsResponse.status === 'fulfilled' ? recentEventsResponse.value : null;
    const recentEvents = Array.isArray(recentEventsData?.data) ? recentEventsData.data : [];

    // Procesar respuestas de formularios recientes - viene en data.data
    const surveyResponsesData = surveyResponsesResponse.status === 'fulfilled' ? surveyResponsesResponse.value : null;
    let surveyResponses = Array.isArray(surveyResponsesData?.data?.data) ? surveyResponsesData.data.data : [];

    // 🔒 APLICAR FILTRADO POR ROL - siguiendo patrón del Profile
    try {
      const userAuth = await getUserAuth();
      const userRole = userAuth?.role || 'profesor';

      console.log(`🔍 DASHBOARD SERVICE: Aplicando filtrado por rol: ${userRole}`);
      console.log(`📊 DASHBOARD SERVICE: Survey responses antes del filtro: ${surveyResponses.length}`);

      // Filtrar survey responses según el rol del usuario
      const filteredSurveyResponses = surveyResponses.filter(response => {
        const category = response.survey?.category || 'otros';
        const canAccess = canUserAccessCategory(userRole, category);

        if (category === 'casos') {
          console.log(`🚨 DASHBOARD SERVICE: Survey #${response.id} categoría CASOS: ${canAccess ? 'PERMITIDO' : 'BLOQUEADO'} para rol ${userRole}`);
          console.log(`   Survey name: ${response.survey?.name}`);
        }

        return canAccess;
      });

      surveyResponses = filteredSurveyResponses;
      console.log(`🔍 DASHBOARD SERVICE: Survey responses después del filtro: ${surveyResponses.length}`);

    } catch (error) {
      console.error('❌ DASHBOARD SERVICE: Error obteniendo rol de usuario:', error);
      // En caso de error, usar rol por defecto 'profesor' y filtrar casos
      surveyResponses = surveyResponses.filter(response => {
        const category = response.survey?.category || 'otros';
        return category !== 'casos'; // Profesor no puede ver casos
      });
      console.log(`🔍 DASHBOARD SERVICE: Filtrado de seguridad aplicado (sin casos): ${surveyResponses.length}`);
    }
    
    // Debug logging para verificar todas las estructuras
    console.log('🔍 DASHBOARD SERVICE: Debug de estructuras de datos:');
    console.log('👥 Users response:', recentUsersResponse.status, 'datos:', recentUsers.length);
    console.log('📅 Events response:', recentEventsResponse.status, 'datos:', recentEvents.length);
    console.log('📋 Survey response:', surveyResponsesResponse.status, 'datos:', surveyResponses.length);
    
    if (surveyResponsesData) {
      console.log('📋 Survey raw data:', surveyResponsesData);
      console.log('📋 Survey data.data:', surveyResponsesData?.data?.data?.length || 'no encontrado');
    }
    
    if (surveyResponses.length > 0) {
      console.log('📋 DASHBOARD SERVICE: Survey responses encontradas:', surveyResponses.length);
      console.log('📋 DASHBOARD SERVICE: Primera response estructura:', surveyResponses[0]);
      console.log('📋 DASHBOARD SERVICE: Survey info:', surveyResponses[0]?.survey);
      console.log('📋 DASHBOARD SERVICE: Respondent info:', surveyResponses[0]?.respondent);
    }

    // Crear timeline unificado con pagos y usuarios
    const unifiedTimeline = [];

    // Agregar pagos al timeline
    console.log('💳 DASHBOARD SERVICE: Agregando pagos al timeline:', recentPayments.length);
    unifiedTimeline.push(...recentPayments.map(payment => ({
      id: `payment_${payment.id}`,
      type: 'payment',
      title: `Pago ${payment.status === 'completed' ? 'completado' : payment.status === 'pending' ? 'pendiente' : payment.status}`,
      description: `${payment.billing?.first_name || 'Usuario'} ${payment.billing?.last_name || ''} - $${parseInt(payment.total || 0).toLocaleString()} CLP`,
      timestamp: payment.date_created || payment.date_paid || new Date().toISOString(),
      status: payment.status,
      icon: 'payment',
      data: payment
    })));
    console.log('💳 DASHBOARD SERVICE: Timeline después de pagos:', unifiedTimeline.length, 'items');

    // Agregar usuarios recientes al timeline (tanto nuevos como actualizados)
    console.log('👥 DASHBOARD SERVICE: Agregando usuarios al timeline:', recentUsers.length);
    unifiedTimeline.push(...recentUsers.map((user, index) => {
      // Determinar si es nuevo o actualizado basado en las fechas
      const dateCreated = new Date(user.date_created || 0);
      const dateModified = new Date(user.date_modified || user.date_created || 0);
      const isNewUser = Math.abs(dateCreated - dateModified) < 60000; // Menos de 1 minuto de diferencia
      
      const userItem = {
        id: `user_${user.id}`,
        type: 'user',
        title: isNewUser ? 'Usuario registrado' : 'Usuario actualizado',
        description: `${user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Usuario'} - ${user.email || 'Sin email'}${user.group_name ? ` (${user.group_name})` : ''}`,
        timestamp: user.date_modified || user.date_created || new Date().toISOString(),
        status: isNewUser ? 'new' : 'updated',
        icon: 'user',
        data: {
          ...user,
          action: isNewUser ? 'registered' : 'updated'
        }
      };
      
      console.log(`👥 DASHBOARD SERVICE: Usuario ${index} creado para timeline:`, userItem.type, userItem.title);
      return userItem;
    }));
    console.log('👥 DASHBOARD SERVICE: Timeline después de usuarios:', unifiedTimeline.length, 'items');

    // Agregar eventos de agenda recientes al timeline
    console.log('📅 DASHBOARD SERVICE: Agregando eventos al timeline:', recentEvents.length);
    unifiedTimeline.push(...recentEvents.map((event, index) => {
      const categoryLabels = {
        clase: 'Clase', reunion: 'Reunión', evaluacion: 'Evaluación',
        actividad: 'Actividad', importante: 'Importante', cita: 'Cita', otro: 'Evento'
      };
      const categoryLabel = categoryLabels[event.category] || 'Evento';
      const eventItem = {
        id: `event_${event.id}`,
        type: 'event',
        title: event.title || 'Sin título',
        description: `${categoryLabel}${event.location ? ` · ${event.location}` : ''}`,
        timestamp: event.created_at || event.start_date || new Date().toISOString(),
        status: event.status || 'scheduled',
        data: event
      };

      console.log(`📅 DASHBOARD SERVICE: Evento ${index} creado para timeline:`, eventItem.type, eventItem.title);
      return eventItem;
    }));
    console.log('📅 DASHBOARD SERVICE: Timeline después de eventos:', unifiedTimeline.length, 'items');

    // Agregar respuestas de formularios recientes al timeline (actividad real)
    console.log('📋 DASHBOARD SERVICE: Procesando survey responses para timeline:', surveyResponses.length);
    unifiedTimeline.push(...surveyResponses.slice(0, 8).map((response, index) => {
      console.log(`📋 DASHBOARD SERVICE: Procesando response ${index}:`, {
        id: response.id,
        survey_name: response.survey?.name,
        respondent: response.respondent,
        created_at: response.created_at
      });
      // Obtener información usando la estructura real del API
      const surveyName = response.survey?.name || 'Formulario sin título';
      const respondentName = response.respondent?.first_name && response.respondent?.last_name 
        ? `${response.respondent.first_name} ${response.respondent.last_name}` 
        : response.respondent?.email || 'Usuario anónimo';
      const category = response.survey?.category || response.survey?.slug || '';
      
      // Obtener información del estudiante desde las respuestas (primera respuesta suele ser el nombre del alumno)
      const studentAnswer = response.responses?.find(r => 
        r.question?.text?.toLowerCase().includes('alumno') || 
        r.question?.text?.toLowerCase().includes('estudiante')
      );
      const studentName = studentAnswer?.answer || '';
      
      // Obtener el curso si está disponible
      const courseAnswer = response.responses?.find(r => 
        r.question?.text?.toLowerCase().includes('curso') || 
        r.question?.text?.toLowerCase().includes('grado')
      );
      const courseName = courseAnswer?.answer || '';
      
      const surveyItem = {
        id: `survey_response_${response.id}`,
        type: 'survey',
        title: 'Formulario completado',
        description: `${surveyName}${studentName ? ` - Estudiante: ${studentName}` : ` - Por: ${respondentName}`}${courseName ? ` (${courseName})` : ''}`,
        timestamp: response.created_at || new Date().toISOString(), // Timestamp real
        status: 'completed',
        icon: 'survey',
        data: {
          ...response,
          survey_category: category,
          student_name: studentName,
          course_name: courseName,
          completion_date: response.created_at,
          respondent_full_name: respondentName
        }
      };
      
      console.log(`📋 DASHBOARD SERVICE: Survey ${index} creado para timeline:`, surveyItem.type, surveyItem.title);
      return surveyItem;
    }));
    console.log('📋 DASHBOARD SERVICE: Timeline después de surveys:', unifiedTimeline.length, 'items');

    // Lógica de selección diversificada: garantizar mezcla de tipos
    const diversifiedTimeline = [];
    
    // Separar por tipos
    const timelineByType = {
      payment: unifiedTimeline.filter(item => item.type === 'payment'),
      user: unifiedTimeline.filter(item => item.type === 'user'),
      event: unifiedTimeline.filter(item => item.type === 'event'),
      survey: unifiedTimeline.filter(item => item.type === 'survey')
    };
    
    // Ordenar cada tipo por timestamp
    Object.keys(timelineByType).forEach(type => {
      timelineByType[type].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    });
    
    console.log('🎯 DASHBOARD SERVICE: Items por tipo antes de selección:', {
      payment: timelineByType.payment.length,
      user: timelineByType.user.length, 
      event: timelineByType.event.length,
      survey: timelineByType.survey.length
    });
    
    // Estrategia de selección balanceada
    // Objetivo: 15 items con balance de tipos
    const maxItems = 15;
    const minPerType = 2; // Mínimo 2 de cada tipo si hay datos
    const remainingSlots = maxItems - (minPerType * 4); // Slots para distribución adicional
    
    // Primera pasada: tomar mínimo de cada tipo
    Object.keys(timelineByType).forEach(type => {
      const items = timelineByType[type].slice(0, minPerType);
      diversifiedTimeline.push(...items);
      console.log(`🎯 DASHBOARD SERVICE: Agregados ${items.length} items de tipo ${type} (mínimo)`);
    });
    
    // Segunda pasada: completar con los tipos que tienen más items disponibles
    let remainingToAdd = maxItems - diversifiedTimeline.length;
    
    // Crear cola de items restantes ordenados por timestamp
    const remainingItems = [];
    Object.keys(timelineByType).forEach(type => {
      const remaining = timelineByType[type].slice(minPerType);
      remainingItems.push(...remaining);
    });
    
    // Ordenar los restantes por timestamp y tomar los más recientes
    remainingItems.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    diversifiedTimeline.push(...remainingItems.slice(0, remainingToAdd));
    
    // Ordenar resultado final por timestamp
    const sortedTimeline = diversifiedTimeline
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, maxItems);

    // Debug final del timeline
    console.log('🎯 DASHBOARD SERVICE: Timeline final creado:', sortedTimeline.length, 'items');
    console.log('🎯 DASHBOARD SERVICE: Tipos en timeline diversificado:', 
      sortedTimeline.reduce((acc, item) => {
        acc[item.type] = (acc[item.type] || 0) + 1;
        return acc;
      }, {})
    );

    return sortedTimeline;

  } catch (error) {
    console.error('❌ DASHBOARD SERVICE: Error en timeline:', error);
    return [];
  }
};

/**
 * Obtiene estadísticas resumidas para los KPIs principales
 * @returns {Promise<Object>} KPIs principales del sistema
 */
export const getDashboardKPIs = async () => {
  try {
    const dashboardData = await getDashboardData();
    
    const kpis = {
      // KPI Usuarios
      totalUsers: {
        value: dashboardData.users.total,
        trend: 'stable', // TODO: Calcular trend comparando con período anterior
        label: 'Usuarios Activos'
      },
      
      // KPI Ingresos
      monthlyRevenue: {
        value: dashboardData.payments.completed,
        trend: 'up',
        label: 'Ingresos del Mes'
      },
      
      // KPI Morosidad
      overdueRate: {
        value: dashboardData.payments.pending > 0 
          ? Math.round((dashboardData.payments.overdue.count / dashboardData.payments.pending) * 100)
          : 0,
        trend: dashboardData.payments.overdue.count > 5 ? 'down' : 'stable',
        label: 'Tasa de Morosidad %'
      },
      
      // KPI Actividad
      weeklyActivity: {
        value: dashboardData.agenda.thisWeek,
        trend: 'up',
        label: 'Eventos Esta Semana'
      }
    };

    return kpis;

  } catch (error) {
    console.error('❌ DASHBOARD SERVICE: Error calculando KPIs:', error);
    return {};
  }
};

/**
 * Limpia el cache del dashboard
 */
export const clearDashboardCache = () => {
  cache.clear();
};

/**
 * Obtiene información del cache para debugging
 */
export const getCacheInfo = () => {
  return {
    size: cache.size,
    keys: Array.from(cache.keys()),
    timestamps: Array.from(cache.values()).map(v => v.timestamp)
  };
};